import { AfterViewInit, Component, ElementRef, HostListener, ViewChild } from '@angular/core';

interface Bullet {
  x: number;
  y: number;
  w: number;
  h: number;
  speed: number;
}

interface Fruit {
  x: number;
  y: number;
  r: number;
  speed: number;
  color: string;
  shape: 'circle' | 'square' | 'triangle' | 'pentagon'; // new
}

@Component({
  selector: 'app-page404',
  templateUrl: './page404.component.html',
  styleUrls: ['./page404.component.scss']
})
export class Page404Component implements AfterViewInit {
  @ViewChild('gameCanvas') gameCanvas!: ElementRef<HTMLCanvasElement>;

  private ctx!: CanvasRenderingContext2D;

  private player = { x: 180, y: 370, w: 40, h: 20, speed: 6 };

  private bullets: Bullet[] = [];
  private fruits: Fruit[] = [];
  private bursts: { x: number; y: number; radius: number; alpha: number }[] = [];

  private score = 0;
  private lastFruitSpawn = 0;
  private gameOver = false;

  private shooting = false;
  private lastShot = 0;
  private shootDelay = 80;

  private movingLeft = false;
  private movingRight = false;
  private moveSpeed = 8;

  private level2 = false;

  ngAfterViewInit(): void {
    this.ctx = this.gameCanvas.nativeElement.getContext('2d')!;
    this.startGame();
  }

  private startGame() {
    this.player.x = 180;
    this.bullets = [];
    this.fruits = [];
    this.bursts = [];
    this.score = 0;
    this.lastFruitSpawn = 0;
    this.level2 = false;
    this.gameOver = false;
    requestAnimationFrame(t => this.update(t));
  }

  private drawPlayer() {
    this.ctx.fillStyle = '#444';
    this.ctx.fillRect(this.player.x, this.player.y, this.player.w, this.player.h);
    this.ctx.fillStyle = 'silver';
    this.ctx.fillRect(this.player.x + this.player.w / 2 - 5, this.player.y - 15, 10, 15);
  }

  private drawBullets() {
    this.ctx.fillStyle = 'green';
    this.bullets.forEach(b => this.ctx.fillRect(b.x, b.y, b.w, b.h));
  }

  private drawFruits() {
    this.fruits.forEach(f => {
      this.ctx.fillStyle = f.color;
      this.ctx.beginPath();
      switch(f.shape) {
        case 'circle':
          this.ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
          this.ctx.fill();
          break;
        case 'square':
          this.ctx.fillRect(f.x - f.r, f.y - f.r, f.r*2, f.r*2);
          break;
        case 'triangle':
          this.ctx.moveTo(f.x, f.y - f.r);
          this.ctx.lineTo(f.x - f.r, f.y + f.r);
          this.ctx.lineTo(f.x + f.r, f.y + f.r);
          this.ctx.closePath();
          this.ctx.fill();
          break;
        case 'pentagon':
          const sides = 5;
          const angle = (2 * Math.PI) / sides;
          this.ctx.moveTo(f.x + f.r * Math.cos(0), f.y + f.r * Math.sin(0));
          for (let i = 1; i <= sides; i++) {
            this.ctx.lineTo(f.x + f.r * Math.cos(i * angle), f.y + f.r * Math.sin(i * angle));
          }
          this.ctx.closePath();
          this.ctx.fill();
          break;
      }
    });
  }
  
  

  private drawScore() {
    this.ctx.fillStyle = 'black';
    this.ctx.font = '16px Arial';
    this.ctx.textAlign = 'left';
    this.ctx.fillText('Score: ' + this.score, 10, 20);
  }

  private drawBursts() {
    this.bursts.forEach((burst, i) => {
      this.ctx.beginPath();
      this.ctx.arc(burst.x, burst.y, burst.radius, 0, Math.PI * 2);
      this.ctx.fillStyle = `rgba(255,0,0,${burst.alpha})`;
      this.ctx.fill();
      burst.radius += 1.5;
      burst.alpha -= 0.05;
      if (burst.alpha <= 0) this.bursts.splice(i, 1);
    });
  }

  private drawGameOver() {
    this.ctx.fillStyle = 'black';
    this.ctx.font = '20px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('Game Over! Score: ' + this.score, 200, 180);
    this.ctx.fillText('Press SPACE to Restart', 200, 210);
  }

  private update(time = 0) {
    const canvas = this.gameCanvas.nativeElement;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (this.gameOver) {
      this.drawGameOver();
      return;
    }

    // Level up at score 100
    if (this.score >= 10 && !this.level2) {
      this.level2 = true;
    }

    // Move player
    if (this.movingLeft && this.player.x > 0) this.player.x -= this.moveSpeed;
    if (this.movingRight && this.player.x + this.player.w < canvas.width) this.player.x += this.moveSpeed;

    // Continuous shooting
    if (this.shooting && time - this.lastShot > this.shootDelay) {
      this.shoot();
      this.lastShot = time;
    }

    this.drawPlayer();
    this.drawBullets();
    this.drawFruits();
    this.drawBursts();
    this.drawScore();

    // Move bullets
    this.bullets.forEach(b => (b.y -= b.speed));
    this.bullets = this.bullets.filter(b => b.y + b.h > 0);

    // Spawn fruits
    if (time - this.lastFruitSpawn > 1000) {
      this.spawnFruit();
      this.lastFruitSpawn = time;
    }

    // Move fruits
    this.fruits.forEach(f => {
      f.y += f.speed;
      // Level 2: smaller and faster
      if (this.level2) {
        f.r = 8;
        f.speed = 3 + Math.random() * 2;
      }
    });

    // Bullet collision
    for (let bi = this.bullets.length - 1; bi >= 0; bi--) {
      const b = this.bullets[bi];
      for (let fi = this.fruits.length - 1; fi >= 0; fi--) {
        const f = this.fruits[fi];
        if (
          b.x < f.x + f.r &&
          b.x + b.w > f.x - f.r &&
          b.y < f.y + f.r &&
          b.y + b.h > f.y - f.r
        ) {
          this.score++;
          this.triggerBurst(f.x, f.y);
          this.fruits.splice(fi, 1);
          this.bullets.splice(bi, 1);
          break;
        }
      }
    }

    // Paddle collision -> burst
    this.fruits.forEach((f, i) => {
      if (
        f.y + f.r >= this.player.y &&
        f.x > this.player.x &&
        f.x < this.player.x + this.player.w
      ) {
        this.triggerBurst(f.x, f.y);
        this.fruits.splice(i, 1);
      }
    });

    // Missed fruit -> Game Over
    if (this.fruits.some(f => f.y - f.r >= canvas.height)) {
      this.gameOver = true;
    }

    requestAnimationFrame(t => this.update(t));
  }

  private spawnFruit() {
    const canvas = this.gameCanvas.nativeElement;
    const x = Math.random() * (canvas.width - 20) + 10;
    const color = this.getRandomColor();
    const shapes: Fruit['shape'][] = ['circle', 'square', 'triangle', 'pentagon'];
    const shape = shapes[Math.floor(Math.random() * shapes.length)];
    this.fruits.push({ x, y: 0, r: 12, speed: 2 + Math.random() * 2, color, shape });
  }
  
  
  // Helper for random color
  private getRandomColor(): string {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  private shoot() {
    this.bullets.push({
      x: this.player.x + this.player.w / 2 - 2,
      y: this.player.y - 10,
      w: 4,
      h: 10,
      speed: 6
    });
  }

  private triggerBurst(x: number, y: number) {
    this.bursts.push({ x, y, radius: 10, alpha: 1 });
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft') this.movingLeft = true;
    if (event.key === 'ArrowRight') this.movingRight = true;
    if (event.key === ' ') {
      if (this.gameOver) this.startGame();
      else this.shooting = true;
    }
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyup(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft') this.movingLeft = false;
    if (event.key === 'ArrowRight') this.movingRight = false;
    if (event.key === ' ') this.shooting = false;
  }
}
